import type { BrowserTestContext } from "@/dev/BrowserTestContext.ts";
import { clickButton, fill } from "@/story-utils/interact/interactUtils.tsx";

const cardInfo = {
    success: {
        name: "John Doe",
        number: "4242424242425678",
        expiration: "11/27",
        security_code: "123",
    },
    invalid_number: {
        name: "John Doe",
        number: "12345",
        expiration: "11/27",
        security_code: "123",
    },
};

type CardType = keyof typeof cardInfo;

export async function fillRequiredBillingAccountFields(c: BrowserTestContext, title = "My billing account") {
    const { step } = c;

    await step("Fill the billing account fields", async () => {
        await fill(c, "Billing account name", title);
        await fill(c, "Invoice email", "mock@warren.io");
        await fill(c, "Contact person's phone", "1234567890");
    });
}

export async function expectBillingAccountDetail(c: BrowserTestContext, title: string) {
    await c.canvas.findByRole("heading", { name: title }, { timeout: 5000 });
}

export function accountCreateInteract(c: BrowserTestContext, cardType: CardType) {
    const { userEvent, canvas, step } = c;

    return {
        async run() {
            await this.fillForm();
            await this.clickCreate();
        },

        async runAndCheck() {
            await this.run();
            await this.checkResult();
        },

        async fillForm() {
            await fillRequiredBillingAccountFields(c);

            await step("Select the Omise payment method", async () => {
                const omiseMethod = await canvas.findByText(/Credit card( \(Omise\)|$)/);
                await userEvent.click(omiseMethod);
            });

            await fillPaymentInfo(c, cardType);
        },

        async clickCreate() {
            await clickButton(c, "Create");
        },

        async checkResult() {
            await step("Wait for the account to be created", async () => {
                if (cardType === "success") {
                    // NB: It takes a while for the popup / verification to progress, so use a longer timeout
                    await canvas.findByText("Payment methods", undefined, { timeout: 9000 });
                    await canvas.findByText("Visa*5678", { exact: false });
                } else {
                    // Find the error toast
                    await canvas.findByText("Adding payment method failed:", { exact: false });
                }
            });
        },
    };
}

export async function fillPaymentInfo(c: BrowserTestContext, cardType: CardType) {
    await c.step(`Fill in payment info (card type ${JSON.stringify(cardType)})`, async () => {
        const info = cardInfo[cardType];

        await fill(c, "Name (Min 4 chars)", info.name);
        await fill(c, "Card number", info.number);
        await fill(c, "Valid thru", info.expiration);
        await fill(c, "CVC", info.security_code);
    });
}
